/**
 * Перенос на другой стол.
 *
 * Гость пересел, компания объединилась, официант ошибся столом —
 * это случается по нескольку раз за вечер. Без переноса приходится
 * отменять заказ и набирать заново: подтверждение менеджера,
 * испорченный отчёт и потерянное время при полном зале.
 *
 * Два способа в одном окне: целиком и поштучно. Переключение
 * не прячем в настройки — официант решает на месте.
 */
import { useEffect, useState } from 'react';
import { formatMoney, lineSum, type OrderState } from '@dastarhan/core';
import { BASE } from '../api';

type Table = { id: string; name: string; busy?: boolean; sum?: number; isVirtual?: boolean };

export function TransferDialog({
  order, token, currentTableId, onTransfer, onCancel,
}: {
  order: OrderState;
  token: string;
  currentTableId: string | null;
  onTransfer: (toTableId: string, itemIds: string[] | null) => Promise<void>;
  onCancel: () => void;
}) {
  // Столы грузим здесь: компонент отвечает за свои данные, а касса
  // не обязана знать про залы ради одного диалога
  const [tables, setTables] = useState<Table[]>([]);
  useEffect(() => {
    void (async () => {
      try {
        const [mapRes, liveRes] = await Promise.all([
          fetch(`${BASE}/hall/map`, { headers: { Authorization: `Bearer ${token}` } }),
          fetch(`${BASE}/hall-plan/live`, { headers: { Authorization: `Bearer ${token}` } }),
        ]);
        // /hall/map отдаёт МАССИВ залов, а не объект с полем halls.
        // Обе формы принимаем: сервер мог измениться, а диалог с пустым
        // списком выглядит как поломка связи, хотя связь в порядке
        const raw = await mapRes.json() as unknown;
        type Hall = { tables: { id: string; name: string; isVirtual?: boolean }[] };
        const halls: Hall[] = Array.isArray(raw)
          ? raw as Hall[]
          : ((raw as { halls?: Hall[] }).halls ?? []);
        const live = liveRes.ok
          ? (await liveRes.json() as { tables: { id: string; state: string; total: number | null }[] })
          : { tables: [] };
        const stateBy = new Map(live.tables.map((t) => [t.id, t]));
        setTables(halls.flatMap((h) => h.tables.map((t) => ({
          id: t.id, name: t.name, isVirtual: t.isVirtual ?? false,
          // Занят — это не только BUSY: стол со счётом (BILL) и стол,
          // откуда зовут (CALL), тоже заняты гостями. Свободен только
          // FREE и бронь, куда ещё не пришли
          busy: !['FREE', 'RESERVED', undefined].includes(stateBy.get(t.id)?.state),
          sum: stateBy.get(t.id)?.total ?? undefined,
        }))));
      } catch {
        /*
         * Связи нет — но столы известны: поднимаем последнюю карту,
         * сохранённую окном выбора стола. Перенос без сети всё равно
         * не уедет (это договор между кассами), зато кассир видит
         * зал и понимает, что дело в связи, а не в пустом списке
         */
        try {
          const raw = localStorage.getItem('dastarhan.hall.cache');
          if (raw) {
            type Hall = { tables: { id: string; name: string; isVirtual?: boolean }[] };
            const halls = JSON.parse(raw) as Hall[];
            setTables(halls.flatMap((h) => h.tables.map((t) => ({
              id: t.id, name: t.name, isVirtual: t.isVirtual ?? false,
              busy: false, sum: undefined,
            }))));
            return;
          }
        } catch { /* битый кэш — падаем в пустой список */ }
        setTables([]);
      }
    })();
  }, [token]);

  const [mode, setMode] = useState<'whole' | 'items'>('whole');
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [toTable, setToTable] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const live = order.items.filter((i) => !i.removed);
  const others = tables.filter((t) => t.id !== currentTableId);

  const toggle = (itemId: string) => {
    setPicked((p) => {
      const next = new Set(p);
      if (next.has(itemId)) next.delete(itemId);
      else next.add(itemId);
      return next;
    });
  };

  const pickedSum = live
    .filter((i) => picked.has(i.itemId))
    .reduce((s, i) => s + lineSum(i), 0);

  const ready = toTable !== null && (mode === 'whole' || picked.size > 0);

  const go = async () => {
    if (!ready) return;
    setBusy(true); setErr('');
    try {
      await onTransfer(toTable!, mode === 'items' ? [...picked] : null);
    } catch (e) {
      /*
       * Честная причина вместо universal «проверьте связь»: кассир
       * должен понимать, звать ли ему интернет или старшего
       */
      const code = (e as { code?: string })?.code ?? '';
      setErr(
        code === 'TABLE_NOT_FOUND' ? 'Такого стола больше нет — обновите схему зала'
          : code === 'BAD_TRANSFER' ? 'На этот стол перенести нельзя'
          : code === 'FORBIDDEN' || code === 'NO_RIGHT' ? 'Перенос доступен старшему'
          : 'Не получилось перенести. Проверьте связь и попробуйте снова',
      );
      setBusy(false);
    }
  };

  return (
    <div className="dialog-backdrop" role="dialog" aria-modal="true" aria-label="Перенос на другой стол">
      <div className="dialog transfer">
        <span className="eyebrow">Зал</span>
        <h1>Перенести на другой стол</h1>

        <div className="transfer-modes">
          <button className={`chip ${mode === 'whole' ? 'on' : ''}`}
            onClick={() => setMode('whole')}>
            Весь заказ
          </button>
          <button className={`chip ${mode === 'items' ? 'on' : ''}`}
            onClick={() => setMode('items')}>
            Отдельные блюда
          </button>
        </div>

        {mode === 'items' && (
          <>
            <p className="hint">Отметьте, что переносим</p>
            <div className="transfer-items">
              {live.map((i) => (
                <button key={i.itemId}
                  className={`transfer-row ${picked.has(i.itemId) ? 'on' : ''}`}
                  onClick={() => toggle(i.itemId)}>
                  <span className="mark">{picked.has(i.itemId) ? '✓' : ''}</span>
                  <span className="nm">{i.name}</span>
                  <span className="qty">{i.qty}</span>
                  <span className="sum">{formatMoney(lineSum(i))}</span>
                </button>
              ))}
            </div>
            {picked.size > 0 && (
              <p className="transfer-total">
                Переносим {picked.size} поз. на {formatMoney(pickedSum)}
              </p>
            )}
          </>
        )}

        {/* Свободные первыми: гость пересаживается за пустой стол,
            и искать его среди занятых официант не должен */}
        <p className="hint">Куда переносим — свободные столы</p>
        <div className="transfer-tables">
          {others.filter((t) => !t.busy && !t.isVirtual).map((t) => (
            <button key={t.id}
              className={`transfer-table ${toTable === t.id ? 'on' : ''}`}
              onClick={() => setToTable(t.id)}>
              <b>{t.name}</b>
              <span>свободен</span>
            </button>
          ))}
          {others.filter((t) => !t.busy && !t.isVirtual).length === 0 && (
            <p className="hint">
              {tables.length === 0
                ? 'Столы не загрузились — проверьте связь с сервером'
                : 'Все столы заняты. Перенести можно на занятый — он ниже'}
            </p>
          )}
        </div>

        {/* Служебный стол: сюда уходит лишнее, когда снять некому.
            Отдельно и с объяснением — это не место, где сидят гости */}
        {others.some((t) => t.isVirtual) && (
          <div className="transfer-virtual">
            {others.filter((t) => t.isVirtual).map((t) => (
              <button key={t.id}
                className={`transfer-table virtual ${toTable === t.id ? 'on' : ''}`}
                onClick={() => setToTable(t.id)}>
                <b>{t.name}</b>
                <span>отложить до администратора</span>
              </button>
            ))}
          </div>
        )}

        {/* Занятые ниже и отдельно: перенести на них можно, но это
            другое действие — объединение двух компаний за одним столом */}
        {others.some((t) => t.busy) && (
          <details className="transfer-busy">
            <summary>Занятые столы · {others.filter((t) => t.busy).length}</summary>
            <div className="transfer-tables">
              {others.filter((t) => t.busy).map((t) => (
                <button key={t.id}
                  className={`transfer-table busy ${toTable === t.id ? 'on' : ''}`}
                  onClick={() => setToTable(t.id)}>
                  <b>{t.name}</b>
                  <span>{t.sum ? formatMoney(t.sum) : 'есть заказ'}</span>
                </button>
              ))}
            </div>
          </details>
        )}

        {toTable && tables.find((t) => t.id === toTable)?.busy && (
          <p className="transfer-warn">
            На этом столе уже есть заказ — позиции добавятся к нему.
          </p>
        )}

        {err && <p className="error">{err}</p>}

        <div className="dialog-actions">
          <button className="btn ghost" onClick={onCancel} disabled={busy}>Отмена</button>
          <button className="btn accent" disabled={!ready || busy} onClick={() => void go()}>
            {busy ? 'Переносим…' : 'Перенести'}
          </button>
        </div>
      </div>
    </div>
  );
}
